import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import Quill from 'quill';
import DOMPurify from 'dompurify';
import 'quill/dist/quill.snow.css';

/**
 * Custom Quill Editor Component
 * 
 * A React wrapper for Quill.js that eliminates React 18 console warnings
 * and provides better control over the editor lifecycle.
 * Includes automatic content sanitization for security.
 * 
 * @param {string} value - HTML content of the editor
 * @param {function} onChange - Callback when content changes (html) => void
 * @param {string} placeholder - Placeholder text
 * @param {boolean} darkMode - Whether to apply dark mode styling
 * @param {boolean} disabled - Whether the editor is read-only
 * @param {object} modules - Custom Quill modules configuration
 * @param {array} formats - Custom Quill formats array
 * @param {string} theme - Quill theme ('snow' or 'bubble')
 * @param {object} style - Additional container styles
 * @param {string} className - Additional CSS classes
 * @param {boolean} sanitizeContent - Whether to sanitize content (default: true)
 */
const QuillEditor = ({ 
  value = '', 
  onChange, 
  placeholder = 'Start typing...', 
  darkMode = false, 
  disabled = false,
  modules,
  formats,
  theme = 'snow',
  style = {},
  className = '',
  sanitizeContent = true
}) => {
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const isUpdatingRef = useRef(false);

  // Sanitize content to prevent XSS and handle special characters
  const sanitizeNotes = useCallback((content) => {
    if (!sanitizeContent || !content || typeof content !== 'string') {
      return content || '';
    }
    
    // Use DOMPurify to sanitize HTML content
    // Allow basic formatting tags that Quill uses
    const cleanContent = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'blockquote'],
      ALLOWED_ATTR: [],
      KEEP_CONTENT: true,
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
      RETURN_DOM_IMPORT: false
    });
    
    return cleanContent;
  }, [sanitizeContent]);

  // Memoize configurations to prevent recreation
  const defaultModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['blockquote'],
      ['clean']
    ],
  }), []);

  const defaultFormats = useMemo(() => [
    'header', 'bold', 'italic', 'underline',
    'list', 'blockquote'
  ], []);

  // Memoize the actual modules and formats to use
  const quillModules = useMemo(() => modules || defaultModules, [modules, defaultModules]);
  const quillFormats = useMemo(() => formats || defaultFormats, [formats, defaultFormats]);

  // Memoize the change handler
  const handleTextChange = useCallback(() => {
    if (isUpdatingRef.current || !quillRef.current) return;
    
    const html = quillRef.current.root.innerHTML;
    const sanitizedHtml = sanitizeNotes(html);
    
    if (onChange) {
      onChange(sanitizedHtml);
    }
  }, [onChange, sanitizeNotes]);

  // Initialize Quill only once
  useEffect(() => {
    if (!containerRef.current || quillRef.current) return;

    // Quill configuration
    const options = {
      theme,
      placeholder,
      readOnly: disabled,
      modules: quillModules,
      formats: quillFormats
    };

    // Create Quill instance
    const quill = new Quill(containerRef.current, options);
    quillRef.current = quill;

    // Set initial content (sanitized)
    if (value) {
      isUpdatingRef.current = true;
      const sanitizedValue = sanitizeNotes(value);
      quill.root.innerHTML = sanitizedValue;
      isUpdatingRef.current = false;
    }

    // Handle text changes
    quill.on('text-change', handleTextChange);

    // Cleanup
    return () => {
      if (quillRef.current) {
        quillRef.current.off('text-change', handleTextChange);
        quillRef.current = null;
      }
    };
  }, []); // No dependencies - initialize only once

  // Update content when value prop changes
  useEffect(() => {
    if (!quillRef.current) return;
    
    const sanitizedValue = sanitizeNotes(value);
    const currentContent = quillRef.current.root.innerHTML;
    
    if (currentContent !== sanitizedValue) {
      isUpdatingRef.current = true;
      quillRef.current.root.innerHTML = sanitizedValue;
      isUpdatingRef.current = false;
    }
  }, [value, sanitizeNotes]);

  // Update placeholder when it changes
  useEffect(() => {
    if (quillRef.current && quillRef.current.root) {
      quillRef.current.root.setAttribute('data-placeholder', placeholder);
    }
  }, [placeholder]);

  // Update disabled state
  useEffect(() => {
    if (quillRef.current) {
      quillRef.current.enable(!disabled);
    }
  }, [disabled]);

  // Apply dark mode and custom styles
  useEffect(() => {
    if (!containerRef.current) return;

    const editor = containerRef.current.querySelector('.ql-editor');
    const toolbar = containerRef.current.querySelector('.ql-toolbar');
    
    if (editor) {
      // Force editor background and text color with !important
      editor.style.setProperty('background-color', darkMode ? '#1f2937' : '#ffffff', 'important');
      editor.style.setProperty('color', darkMode ? '#f3f4f6' : '#111827', 'important');
      
      // Apply custom sizing from style prop
      if (style.maxHeight) {
        editor.style.maxHeight = style.maxHeight;
      } else {
        editor.style.maxHeight = '250px';
      }
      
      if (style.minHeight) {
        editor.style.minHeight = style.minHeight;
      } else {
        editor.style.minHeight = '80px';
      }
      
      editor.style.overflowY = 'auto';
    }
    
    if (toolbar) {
      // Force toolbar background with !important to override Quill's defaults
      toolbar.style.setProperty('background-color', darkMode ? '#1f2937' : '#ffffff', 'important');
      toolbar.style.setProperty('background', darkMode ? '#1f2937' : '#ffffff', 'important');
      toolbar.style.setProperty('border', `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`, 'important');
      toolbar.style.setProperty('border-bottom', `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`, 'important');
      toolbar.style.setProperty('border-top-left-radius', '6px', 'important');
      toolbar.style.setProperty('border-top-right-radius', '6px', 'important');
      
      // Add a CSS class for additional styling
      toolbar.classList.add(darkMode ? 'quill-toolbar-dark' : 'quill-toolbar-light');
      
      // Ensure toolbar buttons are visible
      const buttons = toolbar.querySelectorAll('button, .ql-picker');
      buttons.forEach(button => {
        button.style.setProperty('color', darkMode ? '#f3f4f6' : '#374151', 'important');
      });
      
      // Style icons and SVGs
      const icons = toolbar.querySelectorAll('svg');
      icons.forEach(icon => {
        icon.style.setProperty('fill', darkMode ? '#f3f4f6' : '#374151', 'important');
      });
    }
  }, [darkMode, style]);

  // Default container styles
  const containerStyle = {
    maxHeight: '300px',
    minHeight: '120px',
    ...style
  };

  return (
    <>
      <style>
        {`
          .quill-toolbar-light {
            background-color: #ffffff !important;
            background: #ffffff !important;
            border: 1px solid #d1d5db !important;
          }
          .quill-toolbar-dark {
            background-color: #1f2937 !important;
            background: #1f2937 !important;  
            border: 1px solid #4b5563 !important;
          }
          .ql-toolbar.ql-snow {
            background-color: ${darkMode ? '#1f2937' : '#ffffff'} !important;
            background: ${darkMode ? '#1f2937' : '#ffffff'} !important;
            border: 1px solid ${darkMode ? '#4b5563' : '#d1d5db'} !important;
            border-top-left-radius: 6px !important;
            border-top-right-radius: 6px !important;
          }
          .ql-editor {
            background-color: ${darkMode ? '#1f2937' : '#ffffff'} !important;
            color: ${darkMode ? '#f3f4f6' : '#111827'} !important;
          }
          .ql-editor p, .ql-editor h1, .ql-editor h2, .ql-editor h3, 
          .ql-editor li, .ql-editor blockquote, .ql-editor strong, 
          .ql-editor em, .ql-editor u {
            color: ${darkMode ? '#f3f4f6' : '#111827'} !important;
          }
          .ql-editor.ql-blank::before {
            color: ${darkMode ? '#9ca3af' : '#6b7280'} !important;
          }
          .ql-toolbar button, .ql-toolbar .ql-picker {
            color: ${darkMode ? '#e5e7eb' : '#374151'} !important;
          }
          .ql-toolbar button:hover, .ql-toolbar .ql-picker:hover {
            color: ${darkMode ? '#ffffff' : '#111827'} !important;
          }
          .ql-toolbar button svg, .ql-toolbar .ql-picker svg {
            fill: ${darkMode ? '#e5e7eb' : '#374151'} !important;
            stroke: ${darkMode ? '#e5e7eb' : '#374151'} !important;
          }
          .ql-toolbar button svg .ql-stroke, .ql-toolbar .ql-picker svg .ql-stroke {
            stroke: ${darkMode ? '#e5e7eb' : '#374151'} !important;
          }
          .ql-toolbar button svg .ql-fill, .ql-toolbar .ql-picker svg .ql-fill {
            fill: ${darkMode ? '#e5e7eb' : '#374151'} !important;
          }
          .ql-toolbar button:hover svg, .ql-toolbar .ql-picker:hover svg {
            fill: ${darkMode ? '#ffffff' : '#111827'} !important;
            stroke: ${darkMode ? '#ffffff' : '#111827'} !important;
          }
          .ql-toolbar button:hover svg .ql-stroke, .ql-toolbar .ql-picker:hover svg .ql-stroke {
            stroke: ${darkMode ? '#ffffff' : '#111827'} !important;
          }
          .ql-toolbar button:hover svg .ql-fill, .ql-toolbar .ql-picker:hover svg .ql-fill {
            fill: ${darkMode ? '#ffffff' : '#111827'} !important;
          }
          .ql-toolbar .ql-picker-label, .ql-toolbar .ql-picker-options {
            color: ${darkMode ? '#e5e7eb' : '#374151'} !important;
          }
          .ql-toolbar .ql-picker-options {
            background-color: ${darkMode ? '#1f2937' : '#ffffff'} !important;
            border-color: ${darkMode ? '#4b5563' : '#d1d5db'} !important;
          }
        `}
      </style>
      <div 
        ref={containerRef}
        className={`quill-editor ${className}`}
        style={containerStyle}
      />
    </>
  );
};

export default QuillEditor; 